import { defineStore } from "pinia"
import { computed, ref } from "vue"
import type { InAppNotification } from "@/api/shared/notifications"
import * as api from "@/api/shared/notifications"

/**
 * Inbox notifikasi in-app (Pola Pataniku: state terpusat + lonceng + SSE).
 * ID string mengikuti model backend VideoStreaming.
 */
export const useNotificationsStore = defineStore("notifications", () => {
  const items = ref<InAppNotification[]>([])
  const total = ref(0)
  const unread = ref(0)
  const loading = ref(false)

  const hasUnread = computed(() => unread.value > 0)

  async function refreshUnread(): Promise<void> {
    try {
      unread.value = await api.getUnreadNotificationCount()
    } catch {
      /* badge boleh stale; jangan ganggu UX */
    }
  }

  /**
   * Muat halaman inbox (daftar + total + sinkron unread).
   * @param silent - tanpa spinner (untuk event SSE / inbox_changed).
   */
  async function refresh(opts?: {
    page?: number
    limit?: number
    unreadOnly?: boolean
    silent?: boolean
  }): Promise<void> {
    const page = opts?.page ?? 1
    const limit = opts?.limit ?? 20
    const silent = opts?.silent ?? false
    if (!silent) loading.value = true
    try {
      if (opts?.unreadOnly) {
        const res = await api.listNotificationsInbox({ page, limit, isRead: false })
        items.value = res.data ?? []
        total.value = res.total
      } else {
        const res = await api.listNotifications(page, limit)
        items.value = res.data ?? []
        total.value = res.total
      }
      await refreshUnread()
    } finally {
      if (!silent) loading.value = false
    }
  }

  /** Ringkas daftar 10 teratas untuk dropdown tanpa mengubah total halaman penuh. */
  async function refreshBellPreview(): Promise<void> {
    await refresh({ page: 1, limit: 10, silent: true })
  }

  /** Setelah inbox_changed: hanya refresh ringkas jika cache daftar sudah terisi. */
  async function peekRefreshAfterRemoteEvent(): Promise<void> {
    await refreshUnread()
    if (items.value.length > 0) {
      await refreshBellPreview()
    }
  }

  async function markOneRead(id: string): Promise<void> {
    await api.markNotificationRead(id)
    const target = items.value.find((it) => it.id === id)
    if (target && !target.is_read) {
      target.is_read = true
      target.read_at = new Date().toISOString()
      unread.value = Math.max(0, unread.value - 1)
    }
  }

  async function markAll(): Promise<void> {
    await api.markAllNotificationsRead()
    items.value.forEach((it) => {
      it.is_read = true
      it.read_at = it.read_at ?? new Date().toISOString()
    })
    unread.value = 0
  }

  async function deleteOne(id: string): Promise<void> {
    await api.deleteNotification(id)
    items.value = items.value.filter((it) => it.id !== id)
    total.value = Math.max(0, total.value - 1)
    await refreshUnread()
  }

  async function deleteBulk(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    await api.deleteNotificationsBulk(ids)
    const idSet = new Set(ids)
    items.value = items.value.filter((it) => !idSet.has(it.id))
    total.value = Math.max(0, total.value - ids.length)
    await refreshUnread()
  }

  async function deleteAll(): Promise<void> {
    await api.deleteAllNotifications()
    items.value = []
    total.value = 0
    unread.value = 0
  }

  /** SSE atau sumber lain mengirim item baru (hindari duplikat id). */
  function prependLive(item: InAppNotification): void {
    if (items.value.some((it) => it.id === item.id)) return
    items.value = [item, ...items.value].slice(0, 100)
    if (!item.is_read) {
      unread.value += 1
    }
  }

  function reset(): void {
    items.value = []
    total.value = 0
    unread.value = 0
  }

  return {
    items,
    total,
    unread,
    loading,
    hasUnread,
    refresh,
    refreshUnread,
    refreshBellPreview,
    peekRefreshAfterRemoteEvent,
    markOneRead,
    markAll,
    deleteOne,
    deleteBulk,
    deleteAll,
    prependLive,
    reset,
  }
})
